import { createReducer, on } from '@ngrx/store';
import * as DelegationActions from './delegation.actions';
import { UserDelegationRequestDto } from '../../../api/administration/models/user-delegation-request-dto';
import { GetAllDelegateRequests$Params } from '../../../api/administration/fn/delegations/get-all-delegate-requests';

export interface DelegationState {
  delegations: UserDelegationRequestDto[];
  selectedDelegation: UserDelegationRequestDto | null;
  revokeReasonByDelegationId: Record<number, string>;
  loadingRevokeReasonId: number | null;
  lastLoadParams: GetAllDelegateRequests$Params | null;
  totalRecords: number;
  loading: boolean;
  submitting: boolean;
  createSuccess: boolean;
  error: string | null;
}

export const initialState: DelegationState = {
  delegations: [],
  selectedDelegation: null,
  revokeReasonByDelegationId: {},
  loadingRevokeReasonId: null,
  lastLoadParams: null,
  totalRecords: 0,
  loading: false,
  submitting: false,
  createSuccess: false,
  error: null,
};

export const delegationReducer = createReducer(
  initialState,
  on(DelegationActions.loadDelegationRequests, (state, { params }) => ({
    ...state,
    loading: true,
    lastLoadParams: params,
    error: null,
  })),
  on(DelegationActions.loadDelegationRequestsSuccess, (state, { data, totalRecords }) => ({
    ...state,
    delegations: data,
    totalRecords,
    loading: false,
  })),
  on(DelegationActions.loadDelegationRequestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(DelegationActions.createDelegationRequest, (state) => ({
    ...state,
    submitting: true,
    createSuccess: false,
    error: null,
  })),
  on(DelegationActions.createDelegationRequestSuccess, (state) => ({
    ...state,
    submitting: false,
    createSuccess: true,
  })),
  on(DelegationActions.createDelegationRequestFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(DelegationActions.updateDelegationStatus, (state) => ({
    ...state,
    submitting: true,
    createSuccess: false,
    error: null,
  })),
  on(DelegationActions.updateDelegationStatusSuccess, (state, { response }) => {
    const updatedDelegation = response?.data?.[0] ?? null;

    if (!updatedDelegation?.id) {
      return {
        ...state,
        submitting: false,
        createSuccess: false,
      };
    }

    return {
      ...state,
      submitting: false,
      createSuccess: false,
      delegations: state.delegations.map((item) =>
        item.id === updatedDelegation.id ? { ...item, ...updatedDelegation } : item
      ),
      selectedDelegation:
        state.selectedDelegation?.id === updatedDelegation.id
          ? { ...state.selectedDelegation, ...updatedDelegation }
          : state.selectedDelegation,
    };
  }),
  on(DelegationActions.updateDelegationStatusFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(DelegationActions.loadDelegationById, (state, { params }) => ({
    ...state,
    selectedDelegation: null,
    loadingRevokeReasonId: params.id,
    error: null,
  })),
  on(DelegationActions.loadDelegationByIdSuccess, (state, { response }) => {
    const selectedDelegation = response?.data?.[0] ?? null;
    const delegationId = selectedDelegation?.id;
    const hasDelegationId = typeof delegationId === 'number';

    return {
      ...state,
      selectedDelegation,
      loadingRevokeReasonId: null,
      revokeReasonByDelegationId: hasDelegationId
        ? {
            ...state.revokeReasonByDelegationId,
            [delegationId]: selectedDelegation?.reviewReason ?? selectedDelegation?.reason ?? '',
          }
        : state.revokeReasonByDelegationId,
    };
  }),
  on(DelegationActions.loadDelegationByIdFailure, (state, { error }) => ({
    ...state,
    loadingRevokeReasonId: null,
    error,
  }))
);
